/**
 * AP-773 — function-safe deep clone for Cornerstone annotations. Kept free of
 * Cornerstone / store imports so any step module can use it.
 */
/**
 * Deep copy of an annotation (or its `data`) that drops function-valued
 * properties. Cornerstone's PlanarFreehandROITool stores a callback on every
 * contour it creates (`onInterpolationComplete`), which `structuredClone`
 * rejects — a manual volume draw would otherwise record no step. Typed
 * arrays are copied; everything else is treated as plain data.
 */
export function cloneAnnotationValue<T>(value: T): T {
  if (value === null || typeof value !== 'object') {
    return (typeof value === 'function' ? undefined : value) as T;
  }
  if (ArrayBuffer.isView(value)) {
    return (value as unknown as { slice: () => T }).slice();
  }
  if (Array.isArray(value)) {
    return value.map((v) => cloneAnnotationValue(v)) as T;
  }
  if (value instanceof Date) return new Date(value.getTime()) as T;
  const out: Record<string, unknown> = {};
  Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
    if (typeof v === 'function') return;
    out[k] = cloneAnnotationValue(v);
  });
  return out as T;
}
